import { Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Icon, Grid } from "@mui/material";
import PropTypes from "prop-types";

import InspectionTimeline from "modules/inspections/components/InspectionTimeline";
import SoftBox from "components/SoftComponents/SoftBox";
import { wrapper } from "utils/wrapper";
import { useEffect, useMemo, useState } from "react";
import InspectionPreviewCard from "modules/inspections/components/InspectionPreviewCard";

import inspectionAPI from "modules/inspections/api";
import reportAPI from "services/api/reports";

import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { disableMap } from "utils/map";
import { useSoftUIController } from "hooks/context";
import { useLocation } from "react-router-dom";

import InspectionReportingDialog from "modules/inspections/components/InspectionReportingDialog";

import moment from "moment-timezone";
import fileDownload from "js-file-download";

import { conditionOptions, actionOptions, getLastMonthRange } from "utils/inspection";
import EditInspectionDialog from "modules/inspections/components/EditInspectionDialog";
import Swal from "sweetalert2";

function AllInspectionsView({ map, user }) {
    inspectionAPI.loadToken(useAuth0());
    reportAPI.loadToken(useAuth0());

    const queryClient = useQueryClient();
    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, appearance, userPermissions } = controller;

    const location = useLocation();
    const stateFilter = location?.state?.filter;

    useEffect(() => {
        if (!map.current) return;
        disableMap(map, user, selectedOrg);
    }, [selectedOrg]);

    const [selectedInspection, setSelectedInspection] = useState(null);
    const [visitTypeFilter, setVisitTypeFilter] = useState("all");

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const [reportData, setReportData] = useState(() => {
        const lastMonth = getLastMonthRange();
        return {
            visitType: "callout",
            maintenanceTag: null,
            startDate: lastMonth.start,
            endDate: lastMonth.end,
            conditions: [...conditionOptions],
            actions: [...actionOptions]
        };
    });

    const viewUnresolved = userPermissions.includes("operations:unresolved");
    const showUnderReview = userPermissions.includes("operations:reviewer");
    const factoryAcceptanceTester = userPermissions.includes("operations:fat");
    const canCreateReport = user.user.user_type == "developer";

    const all_inspections = useQuery({
        queryKey: ['getAllInspectionsForUser', user, selectedOrg],
        queryFn: async () => {
            return await inspectionAPI.getAllInspectionsForUser(user);
        }
    });

    const maintenance_tags = useQuery({
        queryKey: ['getAllMaintenanceTags', selectedOrg],
        queryFn: async () => {
            return await inspectionAPI.getAllMaintenanceTags()
        }
    });

    // console.log(all_inspections?.data?.inspections)
    const inspectionsData = all_inspections?.data?.inspections?.filter(i => factoryAcceptanceTester || i.recordType == "inspection") || [];
    const maintenanceTags = maintenance_tags?.data?.filter(t => t.status == 'complete') || [];

    const [statusFilter, setStatusFilter] = useState(() => {
        if (stateFilter) return stateFilter;

        if (viewUnresolved) {
            if (inspectionsData.some(row => row.status === "unresolved")) {
                return "unresolved";
            }
            else if (showUnderReview && inspectionsData.some(row => row.status === "review")) {
                return "review";
            }
        }

        return "all";
    });

    const generateReportMutation = useMutation({
        mutationFn: async (payload) => {
            // console.log(payload)
            return await reportAPI.generateMonthlyInspectionReport(payload.start_time, payload.end_time, payload, { responseType: 'blob' });
        },
        onSuccess: (data, variables) => {
            // variables contains the payload you passed to mutate()

            // Format the dates for a clean filename (e.g. 01082026_to_31082026)
            const formattedStart = moment(variables.start_time).format('DDMMYYYY');
            const formattedEnd = moment(variables.end_time).format('DDMMYYYY');

            var fileName = `InspectionReport_${formattedStart}-${formattedEnd}.pdf`;
            if (variables.visitType == "callout") {
                var fileName = `CalloutReport_${formattedStart}-${formattedEnd}.pdf`;
            }
            else if (variables.visitType == "routine") {
                var fileName = `MaintenanceReport_${variables.maintenanceTag.replace(" ", "-")}.pdf`;
            }

            fileDownload(data, fileName);
            setIsReportModalOpen(false);
        },
        onError: (error) => {
            console.error("Failed to generate report:", error);
        }
    });

    const saveEditedInspection = useMutation({
        mutationFn: async ([data, payload]) => {
            const inspection_record_id = data.metadata._id;
            const original_inspection_id = data.metadata.ascendant_tree[0]?.record?._id || null;
            data.metadata = { ...data.metadata, original_inspection_id };

            payload.append('metadata', JSON.stringify(data.metadata));
            payload.append('newdata', JSON.stringify(data.newdata));

            return await inspectionAPI.editInspection(inspection_record_id, payload);
        },
        onSuccess: async (response, variables) => {
            setIsEditModalOpen(false);

            const [{ metadata, newdata }] = variables;

            const updateDevicesArray = (devices) => {
                if (!Array.isArray(devices)) return devices;
                return devices.map(dev => ({
                    ...dev,
                    conditions: newdata.conditions?.[dev.uuid] !== undefined ? newdata.conditions[dev.uuid] : dev.conditions,
                    actions: newdata.actions?.[dev.uuid] !== undefined ? newdata.actions[dev.uuid] : dev.actions
                }));
            };

            const processTree = (tree) => {
                if (!tree || tree.length === 0) return tree;
                return tree.map(node => {
                    let updatedNode = { ...node, record: { ...node.record } };

                    if (updatedNode.record._id === metadata._id) {
                        updatedNode.record = {
                            ...updatedNode.record,
                            maintenance_tag: newdata.maintenance_tag,
                            recordedAt: newdata.recordedAt,
                            priority: newdata.priority,
                            status: newdata.status
                        };
                        updatedNode.devices = updateDevicesArray(updatedNode.devices);
                    }

                    if (metadata.original_inspection_id && updatedNode.record._id === metadata.original_inspection_id) {
                        updatedNode.record.status = newdata.status;
                    }

                    return updatedNode;
                });
            };

            setSelectedInspection((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    maintenance_tag: newdata.maintenance_tag,
                    recordedAt: newdata.recordedAt,
                    priority: newdata.priority,
                    status: newdata.status,
                    devices: updateDevicesArray(prev.devices),
                    ascendant_tree: processTree(prev.ascendant_tree),
                    descendant_tree: processTree(prev.descendant_tree)
                };
            });

            queryClient.setQueryData(['getAllInspectionsForUser', user, selectedOrg], (oldInspections) => {
                if (!oldInspections || !oldInspections.inspections) return oldInspections;

                return {
                    ...oldInspections,
                    inspections: oldInspections.inspections.map((inspection) => {
                        let updatedInspection = { ...inspection };

                        if (updatedInspection._id === metadata._id) {
                            updatedInspection = {
                                ...updatedInspection,
                                maintenance_tag: newdata.maintenance_tag,
                                recordedAt: newdata.recordedAt,
                                priority: newdata.priority,
                                status: newdata.status,
                                devices: updateDevicesArray(updatedInspection.devices)
                            };
                        }

                        if (metadata.original_inspection_id && updatedInspection._id === metadata.original_inspection_id) {
                            updatedInspection = {
                                ...updatedInspection,
                                status: newdata.status
                            };
                        }

                        updatedInspection.ascendant_tree = processTree(updatedInspection.ascendant_tree);
                        updatedInspection.descendant_tree = processTree(updatedInspection.descendant_tree);

                        return updatedInspection;
                    })
                };
            });

            await Swal.fire({
                icon: "success",
                title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Saved!</div>',
                showConfirmButton: false,
                timer: 2000,
            });
        },
        onError: async (error) => {
            setIsEditModalOpen(false);

            await Swal.fire({
                icon: "error",
                title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Error!</div>',
                html: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">${error.message || "Something went wrong while saving."}</div>`,
                showConfirmButton: true,
                timer: 15000,
            });

            console.error("Failed to edit inspection:", error);
        }
    });

    return (
        <>
            {wrapper((
                <SoftBox>
                    {deviceLayout == "mobile" ? (
                        <SoftBox borderRadius="20px" color={"white"} width="100%" height={'calc(100svh - 4.25rem - 8px)'} minHeight={"calc(100svh - 4.25rem - 8px)"}
                            sx={({ palette, functions: { rgba } }) => ({ backgroundColor: rgba(palette[appearance].background, 0.7), overflowY: 'hidden' })}
                        >
                            {selectedInspection != null ? (
                                <Box m={1} sx={{ height: deviceLayout == 'mobile' ? 'calc(100% - 16px)' : '100%' }}>
                                    <InspectionPreviewCard type={'inspection'} {...{ user, selectedInspection, setSelectedInspection, setIsEditModalOpen }} />
                                </Box>
                            ) : (
                                <SoftBox m={deviceLayout == 'mobile' ? 1 : 0} sx={{ overflowY: 'hidden', borderRadius: '10px', height: deviceLayout == 'mobile' ? 'calc(100% - 16px)' : "100%" }}>
                                    <InspectionTimeline
                                        type={'inspection'}
                                        isLoading={all_inspections.isLoading || maintenance_tags.isLoading}
                                        inspections_data={inspectionsData}
                                        {...{ user, selectedInspection, setSelectedInspection, statusFilter, setStatusFilter, visitTypeFilter, setVisitTypeFilter, setIsReportModalOpen, canCreateReport }}
                                    />
                                </SoftBox>
                            )}
                        </SoftBox>

                    ) : (
                        <SoftBox borderRadius="20px" color={"white"} width="100%" height={"calc(100svh - 5.5rem - 16px)"}
                            sx={({ palette, functions: { rgba } }) => ({ backgroundColor: rgba(palette[appearance].background, 0.7), overflowY: 'hidden' })}
                        >
                            <SoftBox m={1} sx={{ overflowY: 'hidden', borderRadius: '10px', height: 'calc(100% - 16px)' }}>
                                <InspectionTimeline
                                    type={'inspection'}
                                    isLoading={all_inspections.isLoading || maintenance_tags.isLoading}
                                    inspections_data={inspectionsData}
                                    {...{ user, selectedInspection, setSelectedInspection, statusFilter, setStatusFilter, visitTypeFilter, setVisitTypeFilter, setIsReportModalOpen, canCreateReport }}
                                >
                                    <InspectionPreviewCard type={'inspection'} {...{ user, selectedInspection, setSelectedInspection, setIsEditModalOpen }} />
                                </InspectionTimeline>
                            </SoftBox>
                        </SoftBox>
                    )}
                </SoftBox>
            ), controller)}

            <InspectionReportingDialog {...{ reportData, setReportData, isReportModalOpen, setIsReportModalOpen, maintenanceTags, generateReportMutation }} />
            {(selectedInspection && isEditModalOpen) && (
                <EditInspectionDialog {...{ selectedInspection, maintenance_tags, isEditModalOpen, setIsEditModalOpen, saveEditedInspection }} />
            )}
        </>
    );
}

AllInspectionsView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object
}

export default AllInspectionsView;